import React, { useEffect, useMemo, useContext } from 'react';
import {
  APP,
  EVENT_NAME,
  RONGCLOUD_CHATROOM_PREFIX,
} from 'appenv';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch, batch } from 'react-redux';
import { selectExhibitorById, selectExhibitorIds } from 'models/exhibitors';
import moment from 'moment';

import { UserSessionContext } from 'components/UserSession';
import groupChatJoin from 'network/groupChatJoin';
import { addMessage } from 'models/messages';
import { updateConversation } from 'models/conversations';
import CircularLoadingIndicator from 'components/indicators/CircularLoadingIndicator';
import Footer from 'components/footer/Footer';
import BoothProfileContainer from './BoothProfileContainer';
import { BoothProfileTabsContextStateProvider } from './components/BoothProfileTabsContext';

const RootContainerUI = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: auto;
  background-color: ${({ bgColor }) => (bgColor || '#FFFFFF')};
`;

function RootContainer({ children, ...props }) {
  return (
    <>
      <RootContainerUI {...props}>{children}</RootContainerUI>
    </>
  );
}

RootContainer.propTypes = {
  children: PropTypes.node.isRequired,
};

const Container = styled.div`
  width: auto;
  display: block;
  overflow: auto;

  .booth-template-wrapper {
    width: 100%;
    background: ${({ wrapperBgColor }) => wrapperBgColor};
  }
`;

const PolyU618Container = styled(Container)`
  @media (max-width: 960px) {
    margin-top: 56px;
  }
`;

const EmptyState = styled.div`
  padding: 50px;
  text-align: center;
`;

const StyledCircularLoadingIndicator = styled(CircularLoadingIndicator)`
  width: 100%;
  padding: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const FooterWrapper = styled.div`
  padding-top: 20px;
`;

const BoothProfileRootContainer = () => {
  const { boothId } = useParams();
  const boothIds = useSelector(selectExhibitorIds);
  const booth = useSelector((state) => (selectExhibitorById(state, boothId)));
  const { userSession } = useContext(UserSessionContext);
  const dispatch = useDispatch();

  const boothBgColor = useMemo(() => {
    if (APP === 'polyu618') {
      return '#E0E0E0';
    }
    return (booth || {}).booth_bg_color;
  }, [booth]);

  // enable in xtra staging only
  async function sendAutoJoinMsg(groupId) {
    const {
      authToken,
      name,
      customAttendeeFields: {
        vexpo_chat_tag: tag,
        vexpo_chat_user_description: description,
      },
    } = userSession;
    const success = await groupChatJoin(authToken, groupId);
    if (success) {
      const autoMsg = new window.RongIMLib.TextMessage({
        content: 'Has joined the booth',
        user: {
          autoSent: true,
          name,
          tag,
          description,
        },
      });
      const conversationType = window.RongIMLib.ConversationType.GROUP;

      window.RongIMClient.getInstance().sendMessage(conversationType, groupId, autoMsg, {
        onSuccess: (message) => {
          message.id = message.messageUId || message.messageId;
          const messageStr = JSON.stringify(message);
          const messageJSON = JSON.parse(messageStr);
          batch(() => {
            dispatch(addMessage(messageJSON));
            dispatch(updateConversation({
              id: groupId,
              changes: {
                latestMessage: messageJSON,
              },
            }));
          });
        },
        onError: (errorCode) => { console.error('Auto join error'); },
      });
      dispatch(updateConversation({
        id: groupId,
        changes: {
          sentTime: moment().valueOf(),
        },
      }));
    }
  }

  const isEnabledAutoMsg = () => {
    switch (APP) {
      case 'hkstp608':
      case 'paloalto623':
      case 'ms624':
      case 'picox707':
        return false;
      default:
        return true;
    }
  };

  useEffect(() => {
    const locale = 'en';
    if (booth) {
      const translation = booth.translations[locale] || {};
      window.trackingEvent('Booth', 'Enter', `${boothId} - ${translation.name}`);
    }
    if (isEnabledAutoMsg()) {
      sendAutoJoinMsg(`${RONGCLOUD_CHATROOM_PREFIX}_booth_${boothId}`);
    }
  }, [boothId, booth]);

  const hasFunding = booth ? (booth.tags.find((tag) => tag.includes('Stage of Funding: ')) || false) : false;

  const wrapperBgColor = useMemo(() => {
    switch (APP) {
      case 'hkstp608':
        return (hasFunding
          ? 'linear-gradient(180deg, #ACE0F9 -9.96%, #FFF1EB 65.29%, rgba(255, 255, 255, 0) 104.7%)'
          : 'linear-gradient(180deg, #E6D8CC 0%, #FCFBF9 41.63%, #DFE9F3 82.69%, rgba(255, 255, 255, 0) 100%)');
      default:
        return 'initial';
    }
  }, [hasFunding]);

  const BoothContainer = useMemo(() => {
    switch (APP) {
      case 'polyu618':
        return PolyU618Container;
      case 'hkstp608':
      default:
        return Container;
    }
  }, []);

  return (
    <RootContainer bgColor={boothBgColor} id="booth-profile-root">
      <BoothContainer
        id="booth-container"
        wrapperBgColor={wrapperBgColor}
      >
        <BoothProfileTabsContextStateProvider>
          {
            (boothIds && boothIds.length > 0) ? (
              <>
                {
                  booth ? (
                    <BoothProfileContainer
                      booth={booth}
                    />
                  ) : (
                    <EmptyState>
                      <Helmet>
                        <meta charSet="utf-8" />
                        <title>{`Booth Not Found | ${EVENT_NAME}`}</title>
                        <meta name="description" content={`Booth not found in ${EVENT_NAME} event.`} />
                      </Helmet>
                      Booth not found
                    </EmptyState>
                  )
                }
                <FooterWrapper>
                  <Footer hashState={`/booth/${boothId}`} />
                </FooterWrapper>
              </>
            ) : (
              <CircularLoadingIndicator />
            )
          }
        </BoothProfileTabsContextStateProvider>
      </BoothContainer>
    </RootContainer>
  );
};

export default BoothProfileRootContainer;
